import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../api';
import { useReveal } from '../motion';
import { num, str } from './types';

/**
 * Slugy 10 pevných hlavních kategorií, pro které máme ikonu v
 * `public/icons/<slug>.png` (stejná sada jako navigační pruh). Ikona je
 * zelená na průhledném pozadí a přebarvuje se přes `mask` + `currentColor`.
 */
const ICON_SLUGS = new Set([
  'zachran-krmivo',
  'akce',
  'psi',
  'kocky',
  'hlodavci',
  'ptaci',
  'novinky',
  'pro-panicky',
  'teraristika',
  'znacka',
]);

function iconStyle(slug: string): CSSProperties | undefined {
  if (!ICON_SLUGS.has(slug)) return undefined;
  const url = `url("/icons/${slug}.png")`;
  return { maskImage: url, WebkitMaskImage: url };
}

/**
 * Rozcestník kategorií na výloze.
 *
 * Ikonová mřížka 10 hlavních kategorií (strom z `/api/shop/category-tree`,
 * stejný jako navigační pruh) – čisté vektorové ikony místo rastrových
 * obrázků, takže nic nevypadá „rozsypaně". Klik nastaví filtr `?kategorie`
 * a odscrolluje na mřížku zboží – kategorie nemá samostatnou stránku.
 */
export function CategoryStripBlock({ config }: { config: Record<string, unknown> }) {
  const [params, setParams] = useSearchParams();
  const active = params.get('kategorie') ?? '';
  const limit = num(config, 'limit', 10);
  const title = str(config, 'title', 'Nakupujte podle kategorie');

  const [categories, setCategories] = useState<readonly { slug: string; name: string }[]>([]);
  const { ref, className } = useReveal<HTMLElement>();

  useEffect(() => {
    void api
      .listCategoryTree()
      .then((result) => setCategories(result.categories))
      .catch(() => setCategories([]));
  }, []);

  if (categories.length === 0) return null;

  function pick(slug: string) {
    const next = new URLSearchParams(params);
    if (active === slug) next.delete('kategorie');
    else next.set('kategorie', slug);
    setParams(next, { replace: true });

    // Bez odscrollovani by klik vypadal, jako by se nic nestalo – mrizka
    // je pod ohybem stranky.
    document.getElementById('zbozi')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  return (
    <section ref={ref} className={`block categories ${className}`}>
      {title !== '' && <h2 className="block-title">{title}</h2>}
      <div className="category-strip">
        {categories.slice(0, limit).map((category, index) => (
          <button
            key={category.slug}
            type="button"
            className={active === category.slug ? 'category-tile active' : 'category-tile'}
            style={{ '--i': index } as CSSProperties}
            onClick={() => pick(category.slug)}
          >
            {ICON_SLUGS.has(category.slug) ? (
              <span className="category-ico" style={iconStyle(category.slug)} aria-hidden="true" />
            ) : (
              <span className="category-ico is-text" aria-hidden="true">
                {category.name.slice(0, 2)}
              </span>
            )}
            <span className="category-name">{category.name}</span>
          </button>
        ))}
      </div>
    </section>
  );
}
